'use client';

import { TouchEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getPromoEvents } from '@/api/promo-events';
import EventCardPromoted from '@/components/event-cards/event-card-promoted';
import ServiceCardPromoted from '@/components/polako-service-cards/service-card-promoted';
import { WithLocale } from '@/types/platform-data';
import { PromotedEvent } from '@/types/promoted-events';
import { categoryIds, screenBreakpoints } from '@/utils/constants';
import { AssetKey } from '@/utils/image-load-utils';

interface Props {
    promoText: string;
    title: string;
    categoryType: string;
    promoCards: PromotedEvent[];
}

export default function PromoCarousel(props: WithLocale<Props>) {
    const [slideIndex, setSlideIndex] = useState(0);
    const [touchXStart, setTouchXStart] = useState(0);

    const [cards, setCards] = useState(props.promoCards);

    const promoCards = useMemo(() => {
        return cards.map((item: any) => ({
            ...item,
            event: {
                ...item.event,
                name: item.event?.translations[0]?.event_name ?? item.event?.name,
            },
            service: {
                ...item.service,
                name: item.service?.translations[0]?.name ?? item.service?.name,
            },
        }));
    }, [cards]);

    const fetchMoreCards = async () => {
        const promoCards = await getPromoEvents(props.categoryType, props.country?.code || 'RS', props.lang, 30, 1);
        setCards((prev) => [...prev, ...promoCards]);
    };

    useEffect(() => {
        fetchMoreCards();
    }, []);

    const ref = useRef<HTMLDivElement>(null);
    const offsetWidth = 16;

    const slideWidth = useMemo(() => ref.current?.children[0]?.clientWidth, [ref.current?.children[0]]);
    const transformXValue = useMemo(() => {
        if (!slideWidth) return null;
        return -slideIndex * slideWidth - offsetWidth * slideIndex;
    }, [slideWidth, slideIndex]);

    const handleScroll = useCallback(
        (index: number) => {
            setSlideIndex(index);
        },
        [setSlideIndex],
    );

    const onSwipe = (e: TouchEvent) => {
        if (promoCards) {
            const touchXEnd = e.changedTouches[0].clientX;

            const swipeForward = touchXStart > touchXEnd;
            const swipeBack = touchXStart < touchXEnd;

            if (swipeForward)
                setSlideIndex(slideIndex === promoCards.length - 1 ? promoCards.length - 1 : slideIndex + 1);
            if (swipeBack) setSlideIndex(slideIndex === 0 ? 0 : slideIndex - 1);
        }
    };

    const lastSlideClass = (index: number) => (index === slideIndex ? 'lg:min-w-full' : 'lg:min-[90%]');
    const carouselButtonClass = (index: number) => (index === slideIndex ? 'bg-[#757575]' : '');

    useEffect(() => {
        const interval = setInterval(() => handleScroll((slideIndex + 1) % promoCards.length), 3500);

        return () => clearInterval(interval);
    }, [handleScroll, slideIndex, promoCards.length]);

    const renderPromoCards = (categoryType: string, promoCards: any[]) => {
        if (promoCards.length === 0) return null;

        // let updatedPromoCards = [...props.promoCards];

        // if (categoryType === categoryIds.event) {
        //     updatedPromoCards.splice(1, 0, { isExternalCard: false });
        // } else if (categoryType === categoryIds.service) {
        //     updatedPromoCards.splice(1, 0, { isExternalCard: false });
        // }
        const assetKey: AssetKey = window.innerWidth >= screenBreakpoints.sm ? 'promoCardDesktop' : 'promoCardMobile';

        return promoCards.map((item, index) => (
            <div className={`min-w-full touch-none ${lastSlideClass(index)}`} key={index}>
                {categoryType === categoryIds.event && item.event && (
                    <EventCardPromoted
                        assetKey={assetKey}
                        priority={index === 0}
                        lang={props.lang}
                        event={item.event}
                    />
                )}
                {categoryType === categoryIds.service && item.service && (
                    <ServiceCardPromoted assetKey={assetKey} lang={props.lang} service={item.service} />
                )}
                {/*todo use for external cards*/}
                {/*item.isExternalCard ? (*/}
                {/*    <PromoCardExternal lang={props.lang} />*/}
                {/*) : categoryType === categoryIds.event && item.event ? (*/}
                {/*    <EventCardPromoted priority={index === 0} lang={props.lang} event={item.event} />*/}
                {/*) : categoryType === categoryIds.service && item.service ? (*/}
                {/*    <ServiceCardPromoted lang={props.lang} service={item.service} />*/}
                {/*) : null}*/}
            </div>
        ));
    };

    return (
        <>
            <div className="my-4 flex flex-col gap-y-2 md:flex-row md:justify-between">
                <h1 className="text-2xl md:text-3xl">{props.title}</h1>
                {props.promoText && (
                    <h2 className="whitespace-break-spaces text-sm md:text-right md:text-base">{props.promoText}</h2>
                )}
            </div>
            {promoCards.length > 0 && (
                <div
                    onTouchStart={(e) => setTouchXStart(e.touches[0].clientX)}
                    onTouchEnd={onSwipe}
                    className="my-7 flex flex-col"
                >
                    <div
                        ref={ref}
                        className="flex h-52 gap-x-4 transition-transform duration-300 md:h-64"
                        style={{ transform: `translateX(${transformXValue}px)` }}
                    >
                        {renderPromoCards(props.categoryType, promoCards)}
                    </div>

                    <div className="relative flex items-center justify-center py-2">
                        {Array.from({ length: promoCards.length }).map((_, index) => (
                            <div
                                key={index}
                                onClick={() => handleScroll(index)}
                                className="flex h-4 w-4 cursor-pointer items-center justify-center py-4"
                            >
                                <span
                                    className={`block h-2 w-2 cursor-pointer rounded-full border border-[#757575] ${carouselButtonClass(
                                        index,
                                    )}`}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
}
